import React, { useState, useContext } from 'react';
import { CCard, CCol, CRow, CSpinner, CAlert } from '@coreui/react';
import { AuthUserContext } from '../../session/context';
import axios from 'axios';
import { XMLParser } from 'fast-xml-parser';
import config from '../../../utils/EnvironmentSwitcher';
import { toast } from 'react-toastify';

// Import icons for different sections
import allergiesIcon from 'src/assets/icons/allergies-icon.png';
import medicationsIcon from 'src/assets/icons/medications-icon.png';
import problemsIcon from 'src/assets/icons/problems-icon.png';
import proceduresIcon from 'src/assets/icons/procedures-icon.png';
import vitalsIcon from 'src/assets/icons/vitals-icon.png';
import labsIcon from 'src/assets/icons/labs-icon.png';

const CCDViewer = () => {
    const { authUser } = useContext(AuthUserContext);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ccdData, setCcdData] = useState({
        allergies: [],
        medications: [],
        problems: [],
        procedures: [],
        vitals: [],
        labs: []
    });

    // Handle file upload and parsing
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoading(true);
        setError(null);

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const xmlContent = e.target.result;
                const parser = new XMLParser({
                    ignoreAttributes: false,
                    attributeNamePrefix: "@_"
                });

                const parsedData = parser.parse(xmlContent);
                const sections = extractCCDSections(parsedData);
                setCcdData(sections);

                // Store in FHIR
                await storeCCDInFHIR(sections);
            };

            reader.readAsText(file);
        } catch (err) {
            setError('Error parsing CCD document: ' + err.message);
            toast.error('Failed to parse CCD document');
        } finally {
            setLoading(false);
        }
    };

    // Parse allergies from CCD entries
    const parseAllergies = (entries) => {
        if (!entries) return [];
        return entries.map(entry => ({
            substance: entry?.act?.entryRelationship?.observation?.participant?.participantRole?.playingEntity?.name || 'Unknown',
            reaction: entry?.act?.entryRelationship?.observation?.value?.['#text'] || 'Not specified'
        }));
    };

    // Parse medications from CCD entries
    const parseMedications = (entries) => {
        if (!entries) return [];
        return entries.map(entry => ({
            name: entry?.substanceAdministration?.consumable?.manufacturedProduct?.manufacturedMaterial?.name || 'Unknown',
            dosage: entry?.substanceAdministration?.doseQuantity?.['@_value'] || 'Not specified',
            frequency: entry?.substanceAdministration?.effectiveTime?.period?.['@_value'] || 'Not specified'
        }));
    };

    // Convert sections to FHIR resources
    const convertToFHIR = (sections) => {
        const resources = [];
        
        // Convert allergies to FHIR AllergyIntolerance resources
        if (sections.allergies) {
            sections.allergies.forEach(allergy => {
                resources.push({
                    resourceType: 'AllergyIntolerance',
                    code: {
                        text: allergy.substance
                    },
                    reaction: [{
                        manifestation: [{
                            text: allergy.reaction
                        }]
                    }]
                });
            });
        }

        // Convert medications to FHIR MedicationStatement resources
        if (sections.medications) {
            sections.medications.forEach(med => {
                resources.push({
                    resourceType: 'MedicationStatement',
                    medicationCodeableConcept: {
                        text: med.name
                    },
                    dosage: [{
                        text: `${med.dosage} - ${med.frequency}`
                    }]
                });
            });
        }

        return resources;
    };

    // Extract relevant sections from CCD
    const extractCCDSections = (parsedXML) => {
        const sections = {
            allergies: [],
            medications: [],
            problems: [],
            procedures: [],
            vitals: [],
            labs: []
        };

        try {
            // Navigate through CCD structure (simplified example)
            const clinicalDocument = parsedXML.ClinicalDocument;
            const component = clinicalDocument.component.structuredBody.component;

            component.forEach(section => {
                const code = section.section.code['@_code'];
                const entries = section.section.entry || [];

                switch(code) {
                    case '48765-2': // Allergies
                        sections.allergies = parseAllergies(entries);
                        break;
                    case '10160-0': // Medications
                        sections.medications = parseMedications(entries);
                        break;
                    // Add other section parsers
                }
            });
        } catch (err) {
            console.error('Error extracting CCD sections:', err);
        }

        return sections;
    };

    // Store extracted data in FHIR
    const storeCCDInFHIR = async (sections) => {
        try {
            const fhirEndpoint = config.fhirUrl;
            const headers = {
                'Authorization': `Bearer ${authUser.token}`,
                'Content-Type': 'application/json'
            };

            // Convert sections to FHIR resources
            const fhirResources = convertToFHIR(sections);

            // Store each resource type
            for (const [resourceType, resources] of Object.entries(fhirResources)) {
                await axios.post(
                    `${fhirEndpoint}/${resourceType}`,
                    resources,
                    { headers }
                );
            }

            toast.success('Successfully stored CCD data in FHIR');
        } catch (err) {
            toast.error('Failed to store data in FHIR');
            console.error('FHIR storage error:', err);
        }
    };

    return (
        <div className="ccd-viewer">
            <h1 className="dashboard-title">CCD Document Viewer</h1>

            {/* File Upload Section */}
            <CRow className="mb-4">
                <CCol>
                    <CCard className="upload-card">
                        <div className="card-header">
                            <h2>Upload CCD Document</h2>
                        </div>
                        <div className="upload-container">
                            <input
                                type="file"
                                accept=".xml,.ccd"
                                onChange={handleFileUpload}
                                className="file-input"
                            />
                        </div>
                    </CCard>
                </CCol>
            </CRow>

            {/* Loading and Error States */}
            {loading && <CSpinner />}
            {error && <CAlert color="danger">{error}</CAlert>}

            {/* CCD Sections */}
            {!loading && !error && (
                <CRow>
                    {/* Allergies Section */}
                    <CCol xs={isMobile ? '12' : '6'}>
                        <CCard className="dashboard-card">
                            <div className="card-header">
                                <img src={allergiesIcon} alt="Allergies" className="card-icon" />
                                <h2>Allergies</h2>
                                <button className="export-button">Export to FHIR</button>
                            </div>
                            <div className="section-content">
                                {ccdData.allergies.map((allergy, index) => (
                                    <div key={index} className="section-item">
                                        <h3>{allergy.substance}</h3>
                                        <p>Severity: {allergy.severity}</p>
                                        <p>Status: {allergy.status}</p>
                                    </div>
                                ))}
                            </div>
                        </CCard>
                    </CCol>

                    {/* Add similar cards for other sections */}
                </CRow>
            )}
        </div>
    );
};

export default CCDViewer; 